import { BodyText, Skeleton } from "@/components/ui";
import { Button } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { roomActionsInProgressAtom } from "@/features/Charting/atoms/roomActions";
import { usePatientDemographics } from "@/features/Patient/PatientBar/network/usePatientDemographics";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { constants } from "@curai/js-fhir-tools";
import { useParams, useSearch } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { Video } from "lucide-react";
import { useChatInput } from "../hooks/useChatInput";
import {
  ChatActionType,
  REASON_LABELS,
  useChatAvailableActions,
} from "../network/useChatAvailableActions";
import { ChatFilterMode } from "../types/chatMessageTypes";
import { ChatModeIndicator } from "./ChatModeIndicator";
import { DictationTextarea } from "./DictationTextarea";

// Constants
const BUTTON_SEND = "Send";
const TRANSLATE_AND_SEND = "Translate & Send";

/**
 * ChatInputBar component
 *
 * Renders the message input and send button at the bottom of the chat interface.
 * Handles user input, typing indicators, and message sending by leveraging the useChatInput hook.
 *
 * The component is designed to be fully functional with minimal props - it only needs the room token
 * to identify the chat room.
 *
 * @param roomToken - The token identifying the chat room
 * @param filterMode - The current filter mode (Live, Async, Combined)
 * @param effectiveChatMode - The effective chat mode based on filter selection
 */
interface ChatInputBarProps {
  roomToken: string | undefined;
  toggleVideoVisitCall: () => void;
  isCallPending: boolean;
  filterMode?: ChatFilterMode;
  effectiveChatMode?: ChatActionType;
}

export const ChatInputBar = ({
  roomToken,
  toggleVideoVisitCall,
  isCallPending,
  filterMode = ChatFilterMode.COMBINED,
  effectiveChatMode,
}: ChatInputBarProps) => {
  const { patientId } = useParams({ strict: false });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const { data: chatFilterEnabled } = useConfigCatFeatureFlag("decouple-chat");

  const { data: patient } = useGetPatient(patientId as string);
  const preferredLanguage =
    patient?.preferredLanguage || constants.LanguageCode.ENGLISH;

  const { data: demographics } = usePatientDemographics(patientId as string);
  const careNotEstablished = demographics?.administrative?.careNotEstablished;

  // Subscribe to room actions state
  const isRoomActionInProgress = useAtomValue(roomActionsInProgressAtom);

  const {
    data: { actions, chatMode } = {},
    isPending: isAvailableActionsPending,
  } = useChatAvailableActions({
    patientId: patientId as string,
    encounterId: encounterId as string | undefined,
  });

  // Use effectiveChatMode if provided, otherwise fall back to chatMode from API
  const activeChatMode = effectiveChatMode ?? chatMode;

  const { messageText, handleSendMessage, handleKeyDown, handleTextChange } =
    useChatInput({ roomToken, chatMode: activeChatMode });

  /**
   * Check if an action is blocked.
   * An action is blocked if:
   * 1. It's not in the API response at all (undefined means not available for this context)
   * 2. It has unavailable reasons (length > 0)
   */
  const isActionBlocked = (action: ChatActionType) => {
    if (isAvailableActionsPending) return false;

    // If the action is not in the response, it's blocked
    if (actions?.[action] === undefined) return true;

    // If the action has unavailable reasons, it's blocked
    return actions[action].length > 0;
  };

  let determinedUnavailableReason: string | undefined;

  if (isRoomActionInProgress) {
    determinedUnavailableReason = "Room action in progress";
  } else if (!activeChatMode) {
    // Handles cases where activeChatMode is falsy (e.g., undefined, null, empty string)
    // indicating chat features might not be configured or available.
    determinedUnavailableReason = "Chat is currently unavailable";
  } else if (isActionBlocked(activeChatMode)) {
    // activeChatMode is defined, but the specific action for this mode is not permitted.
    const reasonCode = actions?.[activeChatMode]?.[0];
    determinedUnavailableReason = reasonCode
      ? REASON_LABELS[reasonCode]
      : "Chat restricted"; // Fallback if no specific reason code from backend or mapping.
  }
  // If none of the above conditions were met, determinedUnavailableReason remains undefined,
  // meaning the chat is available.

  const isChatUnavailable = Boolean(determinedUnavailableReason);
  // This variable is used in the placeholder text.
  const chatUnavailableReason = determinedUnavailableReason;

  const getPlaceholderText = () => {
    // Async tab selected but async is not available (UC/Amazon patients)
    // Check this FIRST before generic unavailable check
    if (
      chatFilterEnabled &&
      filterMode === ChatFilterMode.ASYNC &&
      isActionBlocked(ChatActionType.ASYNC_CHAT)
    ) {
      return "Async chat not available";
    }

    if (isChatUnavailable) {
      return `Chat is unavailable: ${chatUnavailableReason}`;
    }

    // Mode-specific placeholder text only when feature flag is enabled
    if (chatFilterEnabled) {
      if (
        filterMode === ChatFilterMode.LIVE ||
        filterMode === ChatFilterMode.COMBINED
      ) {
        return "Type a live message...";
      }
      if (filterMode === ChatFilterMode.ASYNC) {
        return "Type an async message...";
      }
    }

    return "Type a message...";
  };

  const isSendButtonDisabled = !messageText.trim() || isRoomActionInProgress;

  if (isAvailableActionsPending) {
    return (
      <div className="py-3 px-4 border-t flex gap-2 items-center">
        <Skeleton className="h-10 flex-1" />
        <Skeleton className="h-8 w-14" />
      </div>
    );
  }

  const inputContent = (
    <>
      {careNotEstablished &&
        encounterId &&
        !isActionBlocked(ChatActionType.VIDEO_CALL) && (
          <div className="bg-warning p-2 leading-none rounded-md">
            <BodyText size="sm" textColor="warning">
              Must start an Establish Care Video Call. Click Video icon below to
              start.
            </BodyText>
          </div>
        )}

      <div className="flex gap-2 items-center">
        {encounterId && !isActionBlocked(ChatActionType.VIDEO_CALL) && (
          <Button
            size="sm"
            variant="icon"
            onClick={toggleVideoVisitCall}
            disabled={isCallPending}
            data-testid="video-call-button"
          >
            <Video className="w-4 h-4" />
          </Button>
        )}
        <DictationTextarea
          value={messageText}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholderText()}
          disabled={isChatUnavailable}
          disableDictation={isChatUnavailable}
          encounterId={encounterId as string | undefined}
          className="max-h-64"
          trackerStart={TRACKERS.DICTATION_CHAT_START}
          trackerEnd={TRACKERS.DICTATION_CHAT_END}
          data-testid="chat-input-textbox"
        />
        <Button
          size="sm"
          onClick={handleSendMessage}
          disabled={isSendButtonDisabled}
        >
          {preferredLanguage === constants.LanguageCode.ENGLISH
            ? BUTTON_SEND
            : TRANSLATE_AND_SEND}
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex flex-col gap-2 py-3 px-4 border-t">
      {chatFilterEnabled ? (
        <ChatModeIndicator filterMode={filterMode}>
          {inputContent}
        </ChatModeIndicator>
      ) : (
        inputContent
      )}
    </div>
  );
};
